"use strict";
/**
 * Resolves a structured relative date-time expression object into a human-readable string.
 *
 * @param {Object} value - An object containing the relative date-time expression components.
 * @param {string} [value.amount] - The numeric value (as a string) representing the quantity, optionally prefixed by an operator (`+` or `-`).
 * @param {string} [value.unit] - The time unit (e.g., "days", "weeks"), which may include a leading "-" to indicate the past.
 * @returns {string | null} - A formatted string representing the relative date-time expression, or `null` if the input is invalid.
 *
 *
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveInitialFunctionFromUnit = exports.restrictToNumericValues = exports.isValidInitialFunctionUnit = exports.temporalFunctionToRelativeUnit = exports.resolveExpressionFromRelativeDateTime = void 0;
const resolveExpressionFromRelativeDateTime = (value) => {
    const initialFunction = !value?.startOfUnit ? "now" : (0, exports.resolveInitialFunctionFromUnit)(value?.unit);
    const operator = !value?.unit?.includes("-") ? " + " : " - ";
    const amount = !value?.amount ? "" : value?.amount;
    const temporalFunction = !value?.unit ? "" : value?.unit?.replace("-", "");
    if (value?.amount && value?.unit) {
        return `${initialFunction}${operator}${amount} ${temporalFunction}`;
    }
    if (!value?.amount && value?.unit)
        return temporalFunction;
    if (!value?.unit && value?.amount)
        return `${amount}`;
    return null;
};
exports.resolveExpressionFromRelativeDateTime = resolveExpressionFromRelativeDateTime;
/**
 * Converts a temporal function string to a relative time unit string.
 *
 * @param {string} temporalFunction - The input string representing a temporal function (e.g., "seconds", "minutes", "hours").
 * @param {boolean} isPast - A boolean flag indicating whether the time unit should represent the past (true) or the future (false).
 * @returns {string | null} - A string representing the relative time unit (e.g., "-seconds", "minutes") or `null` if the input is invalid.
 *
 */
const temporalFunctionToRelativeUnit = (temporalFunction, isPast) => {
    if (typeof temporalFunction !== "string")
        return null;
    const value = temporalFunction.toLowerCase().trim();
    const prefix = isPast ? "-" : "";
    if (["second", "s", "sec", "seconds"].includes(value))
        return `${prefix}seconds`;
    if (["minute", "m", "min", "minutes"].includes(value))
        return `${prefix}minutes`;
    if (["hour", "h", "hr", "hours"].includes(value))
        return `${prefix}hours`;
    if (["day", "d", "days"].includes(value))
        return `${prefix}days`;
    if (["week", "w", "weeks"].includes(value))
        return `${prefix}weeks`;
    if (["month", "months"].includes(value))
        return `${prefix}months`;
    if (["year", "y", "yr", "years"].includes(value))
        return `${prefix}years`;
    if (["quarter", "quarters"].includes(value))
        return `${prefix}quarters`;
    return null;
};
exports.temporalFunctionToRelativeUnit = temporalFunctionToRelativeUnit;
/**
 * Checks if the provided unit is supported as an InitialFunction on the backend https://docs.mnemonic.no/api/general_integration_guide/08-time_fields.html#relative-time-format
 * @param unit string
 * @returns boolean
 */
const isValidInitialFunctionUnit = (unit) => {
    const VALID_INITIAL_FUNCTION_UNIT = ["-days", "-weeks", "-months", "-years", "days", "weeks", "months", "years"]; // These units can be used with initialFunction e.g startOfDay, startOfWeek
    return VALID_INITIAL_FUNCTION_UNIT?.includes(unit);
};
exports.isValidInitialFunctionUnit = isValidInitialFunctionUnit;
/**
 * A function that can be used to restrict a users input to only include numeric values.
 * @param value string
 * @returns number || undefined
 */
const restrictToNumericValues = (value) => {
    // regex that finds and removes all non-digit characters, restricting the user to only type in numeric inputs
    const numericValue = value.replace(/\D/g, "");
    return numericValue !== "" ? Number(numericValue) : undefined;
};
exports.restrictToNumericValues = restrictToNumericValues;
/**
 * Resolves the initial function name based on the provided time unit.
 *
 * @param unit  string
 * @returns string
 *
 */
const resolveInitialFunctionFromUnit = (unit) => {
    if (!(0, exports.isValidInitialFunctionUnit)(unit))
        return "";
    if (unit === "days" || unit === "-days")
        return "startOfDay";
    if (unit === "weeks" || unit === "-weeks")
        return "startOfWeek";
    if (unit === "months" || unit === "-months")
        return "startOfMonth";
    if (unit === "years" || unit === "-years")
        return "startOfYear";
    return "";
};
exports.resolveInitialFunctionFromUnit = resolveInitialFunctionFromUnit;
